import 'lazysizes';
import { createApp } from 'vue';
import { createStore } from 'vuex';

import { hoistJsonScripts } from '@Foundation/utilities';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import Foundation from '@Foundation';
import LoadScriptModule from '@Foundation/loadscript';
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { tag } from '@Foundation/analyticsHandler/directives/tag';
import AccessibilityStore from '@Foundation/accessibilityStore';

// Feature
import AlertModule from '@Feature/Alert';
import CookieBanner from '@Feature/PageContentExt';
import GenericForm from '@Feature/GenericForm';
import HaircareDiag from '@Feature/HaircareDiag';
import HaircolorDiag from '@Feature/HaircolorDiag';
import HaircolorTreat from '@Feature/HaircolorTreat';
import HeaderLogo from '@Feature/HeaderLogo';
import Iframepopup from '@Feature/IframePopup';
import InstagramComponent from '@Feature/Instagram';
import likebutton from '@Feature/LikeButton';
import ModalModule from '@Feature/Modal';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBadge from '@Feature/OapBadge';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapCard from '@Feature/OapCard';
import OapCardPopin from '@Feature/OapCardPopin';
import OapCarouselControls from '@Feature/CarouselControls';
import OapContrastSwitcher from '@Feature/OapContrastSwitcher';
import OapCountdown from '@Feature/OapCountdown';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapFlowplayer from '@Feature/OapFlowplayer';
import OapFocusTrap from '@Feature/OapFocusTrap';
import OapGeolocationPopin from '@Feature/OapGeolocationPopin';
import OapIframe from '@Feature/OapIframe';
import OapLoader from '@Feature/OapLoader';
import OapMarkupRenderingDelay from '@Feature/OapMarkupRenderingDelay';
import OapMediaCarousel from '@Feature/OapMediaCarousel';
import OapPopin from '@Feature/OapPopin';
import OapPromoBar from '@Feature/OapPromoBar';
import OapRating from '@Feature/OapRating';
import OapRoutineBuilder from '@Feature/RoutineBuilder';
import OapScroller from '@Feature/OapScroller';
import OapSlider from '@Feature/OapSlider';
import OapStoriesComponent from '@Feature/OapStories';
import OapStrikethroughScrolling from '@Feature/OapStrikethroughScrolling';
import OapTabComponent from '@Feature/OapTab';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Sfmc from '@Feature/SFMC';
import Slider from '@Feature/Slider';
import Toasted from '@Feature/OapToastNotification/code/plugin';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import VirtualTryOn from '@Feature/VirtualTryOn';
import VueModels from '@Feature/VueModels';
import OapPageNavigation from '@Feature/OapPageNavigation';
import OapPromoCard from '@Feature/OapPromoCard';
import FooterNewsletterSignupModal from '@Feature/FooterNewsletterSignupModal';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';
import OapDsfAppWrapper from '@Feature/OapDsfAppWrapper';

/**
 * Used only on WoW
 * TODO: Include at `women-of-worth.js` level and remove from all other entrypoints/bundles
 */
import CarouselModule from '@Feature/Carousel';

/**
 * This component is required only on PDP but is included at root level
 *
 * TODO: Include/bind component at root PDP container, include in PDP entry/bundle
 * and remove from everywhere else
 */
import OapToastNotification from '@Feature/OapToastNotification';

// 3rd party
import { Tabs, Tab } from 'vue3-tabs-component';

exposeGlobals();

const app = createApp({});
// TODO: To be enabled if removing imports for eventBus makes sense
// app.config.globalProperties.eventBus = eventBus;
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

const VueTabsPlugin = {
  install(app) {
    app.component('Tabs', Tabs);
    app.component('Tab', Tab);
  },
};

// Configure Vue instance
app.use(Toasted);
app.use(VueTabsPlugin);

// Register directives
Object.entries({
  ...Foundation.directives,
  ...NavigationModule.directives,
  ...LoadScriptModule.directives,
  ...Search.directives,
  tag,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register component
// TODO: Object.values ???
Object.entries({
  ...AlertModule.components,
  ...CookieBanner.components,
  ...Foundation.components,
  ...GenericForm.components,
  ...HaircareDiag.components,
  ...HaircolorDiag.components,
  ...HaircolorTreat.components,
  ...HeaderLogo.components,
  ...Iframepopup.components,
  ...InstagramComponent.components,
  ...likebutton.components,
  ...LoadScriptModule.components,
  ...ModalModule.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...OapAccountNavigation.components,
  ...OapBadge.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapCard.components,
  ...OapCardPopin.components,
  ...OapCarouselControls.components,
  ...OapContrastSwitcher.components,
  ...OapCountdown.components,
  ...OapEventfulTextInput.components,
  ...OapFlowplayer.components,
  ...OapFocusTrap.components,
  ...OapGeolocationPopin.components,
  ...OapIframe.components,
  ...OapLoader.components,
  ...OapMarkupRenderingDelay.components,
  ...OapMediaCarousel.components,
  ...OapPopin.components,
  ...OapPromoBar.components,
  ...OapRating.components,
  ...OapRoutineBuilder.components,
  ...OapScroller.components,
  ...OapSlider.components,
  ...OapStoriesComponent.components,
  ...OapStrikethroughScrolling.components,
  ...OapTabComponent.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...SearchSuggestions.components,
  ...Sfmc.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...VirtualTryOn.components,
  ...VueModels.components,
  ...CarouselModule.components,
  ...OapToastNotification.components,
  ...OapPageNavigation.components,
  ...OapPromoCard.components,
  ...FooterNewsletterSignupModal.components,
  ...FloatingSidebar.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...WishList.components,
  ...OapDsfAppWrapper.components,
}).forEach(([name, component]) => app.component(name, component));

Object.entries({
  ...Foundation.filters,
}).forEach(([name, filter]) => app.filter(name, filter));

const store = createStore({
  plugins: [...VirtualTryOn.plugins],
  modules: {
    ...OapTabComponent.modules,
    ...VirtualTryOn.modules,
    ...AccessibilityStore.modules,
  },
});

app.use(store);

/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, store);
// Initialize third party loader
init();

requireAll(require.context('../../../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);
window.RefAppMctb && app.use(window.RefAppMctb);
window.RefAppAmp && app.use(window.RefAppAmp);
window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);
window.RefAppFaq && app.use(window.RefAppFaq);
window.RefAppRatingsReviews && app.use(window.RefAppRatingsReviews);
window.RefAppBackToTop && app.use(window.RefAppBackToTop);
window.RefAppUserRedirect && app.use(window.RefAppUserRedirect);
globalThis.RefAppBeforeAfter && app.use(globalThis.RefAppBeforeAfter);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
